// ─── ApiRequestError ─────────────────────────────────────────────────────────
// Real Error subclass for failed API envelopes. The service layer used to `throw`
// bare object literals shaped like ApiError, which meant no stack trace, no
// `instanceof` check, and anything catching with `err instanceof Error` silently
// missed them (the `no-throw-literal` lint rule exists for exactly this).
//
// The constructor takes the same object shape the literals used, so call sites keep
// their field names and every existing `catch (err) { (err as ApiError).message }`
// keeps working — `message` is just Error's own property now.

import type { ApiError } from '../types';

interface ApiRequestErrorInit {
  message: string;
  code?: string;
  statusCode?: number;
  details?: Record<string, string[]>;
  /** Raw per-field envelope errors, when the caller passed them through. */
  errors?: unknown;
}

export class ApiRequestError extends Error implements ApiError {
  readonly code: string;
  readonly statusCode: number;
  readonly details?: Record<string, string[]>;
  readonly errors?: unknown;

  constructor(init: ApiRequestErrorInit) {
    super(init.message);
    this.name = 'ApiRequestError';
    this.code = init.code ?? 'API_ERROR';
    this.statusCode = init.statusCode ?? 0;
    this.details = init.details;
    this.errors = init.errors;

    // Required for `instanceof` to hold when targeting ES5, which CRA still does.
    Object.setPrototypeOf(this, ApiRequestError.prototype);
  }
}
